import { AgGridTooltip } from "@/components/ag-grid-tooltip";
import { AddButton } from "@/components/buttons/add-button";
import {
	ErrorModal,
	HelmetDexteel,
	useHasPermission,
} from "@dexteel/mesf-core";
import DeleteIcon from "@mui/icons-material/Delete";
import FormatListBulletedSharpIcon from "@mui/icons-material/FormatListBulletedSharp";
import {
	Alert,
	Box,
	Button,
	Grid2,
	Paper,
	Snackbar,
	Typography,
	useTheme,
} from "@mui/material";
import { globalDefaultColDef } from "@setup/ag-grid/mesf-theme";
import { useQueryClient } from "@tanstack/react-query";
import type {
	ColDef,
	GetContextMenuItemsParams,
	ICellRendererParams,
	MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useState } from "react";
import {
	Group,
	Panel,
	Separator,
	useDefaultLayout,
} from "react-resizable-panels";
import { useNavigate } from "react-router-dom";
import { AddTaskListArea } from "./components/AddTaskListArea";
import { RemoveTaskListArea } from "./components/RemoveTaskListArea";
import {
	TASK_LIST_AREAS_QUERY_KEYS,
	useTaskListAreas,
} from "./hooks/useTaskListAreas";
import type { DefaultArea, TaskListArea } from "./models/TaskListArea";

const PLANT_ASSET_ID = 0;
const DEFAULT_LAYOUT = { left: 38, right: 62 };

// One line per row: the global autoHeight overlaps rows when the grid sits in a resizable panel
const singleLine = { autoHeight: false, wrapText: false } as const;

const getCompactPath = (fullPath: string) => {
	if (!fullPath) return "";
	const parts = fullPath.split("/");
	if (parts.length <= 3) return fullPath;
	return `${parts[0]}/.../${parts[parts.length - 2]}/${parts[parts.length - 1]}`;
};

// Same look as the asset links on Asset Details: opens the task list of the area, which is the
// quickest way to check what a change did.
const PathLinkCell = ({ data }: ICellRendererParams<TaskListArea>) => {
	const theme = useTheme();
	const navigate = useNavigate();
	if (!data) return null;

	return (
		<Box
			component="span"
			onClick={() => navigate(`/task-list/area/${data.AssetId}`)}
			sx={{
				color: theme.palette.primary.main,
				cursor: "pointer",
				"&:hover": { textDecoration: "underline" },
			}}
		>
			{getCompactPath(data.FullPath)}
		</Box>
	);
};

export const TaskListAreasPage = () => {
	const queryClient = useQueryClient();
	const hasPermissionTo = useHasPermission();
	const canEdit = hasPermissionTo("MMS.SetTaskListArea");
	const navigate = useNavigate();

	const { defaultLayout: savedLayout, onLayoutChange } = useDefaultLayout({
		id: "task-list-areas",
		storage: localStorage,
	});

	const [defaultAreaId, setDefaultAreaId] = useState<number>(PLANT_ASSET_ID);
	const [adding, setAdding] = useState<DefaultArea | null>(null);
	const [removing, setRemoving] = useState<TaskListArea | null>(null);
	const [notification, setNotification] = useState<string>("");
	const [dismissedError, setDismissedError] = useState<unknown>(null);

	const { data, isLoading, isFetching, error } =
		useTaskListAreas(defaultAreaId);
	const areas = data?.areas ?? [];
	const defaultAreas = data?.defaultAreas ?? [];
	const selected =
		defaultAreas.find((d) => d.AssetId === defaultAreaId) ?? null;

	const defaultAreaColumns = useMemo<ColDef<DefaultArea>[]>(
		() => [
			{
				field: "AssetName",
				headerName: "Default Area",
				flex: 2,
				minWidth: 140,
				cellStyle: { textAlign: "left" },
				tooltipComponent: "agGridTooltip",
				tooltipValueGetter: (p) => p.data?.FullPath ?? "",
				...singleLine,
			},
			{
				field: "Users",
				headerName: "Users",
				width: 90,
				...singleLine,
			},
			{
				field: "AreasShown",
				headerName: "Areas",
				width: 90,
				tooltipComponent: "agGridTooltip",
				tooltipValueGetter: () => "Sections its task list shows",
				...singleLine,
			},
		],
		[],
	);

	const areaColumns = useMemo<ColDef<TaskListArea>[]>(
		() => [
			{
				field: "AssetName",
				headerName: "Area",
				flex: 2,
				minWidth: 140,
				cellStyle: { textAlign: "left" },
				...singleLine,
			},
			{
				field: "FullPath",
				headerName: "Path",
				flex: 3,
				minWidth: 200,
				cellStyle: { textAlign: "left" },
				cellRenderer: PathLinkCell,
				tooltipComponent: "agGridTooltip",
				tooltipValueGetter: (p) =>
					p.data ? `${p.data.FullPath} (open its task list)` : "",
				...singleLine,
			},
			{
				field: "AlsoShownIn",
				headerName: "Shown For",
				flex: 2,
				minWidth: 140,
				tooltipComponent: "agGridTooltip",
				tooltipValueGetter: (p) =>
					p.data?.AlsoShownIn
						? `Default areas that show it: ${p.data.AlsoShownIn}`
						: "",
				...singleLine,
			},
			{
				headerName: "",
				width: 130,
				maxWidth: 130,
				sortable: false,
				filter: false,
				...singleLine,
				// Same actions cell as the mesf-core configuration grids: shown on hover or selection
				cellRenderer: (params: ICellRendererParams<TaskListArea>) => (
					<Grid2
						container
						sx={{
							height: "100%",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							opacity: 0,
							transition: "opacity 0.1s ease-in-out",
							".ag-row-hover &": { opacity: 1 },
							".ag-row-focus &": { opacity: 1 },
							".ag-row-selected &": { opacity: 1 },
						}}
					>
						{canEdit && (
							<Button
								aria-label="remove area"
								onClick={() => setRemoving(params.data ?? null)}
							>
								<DeleteIcon color="secondary" />
							</Button>
						)}
						<Button
							aria-label="actions"
							onClick={(e) =>
								// biome-ignore lint/suspicious/noExplicitAny: mouseEvent is missing from the typings
								(params.api as any).showContextMenu({
									column: params.column,
									rowNode: params.node,
									mouseEvent: e.nativeEvent,
								})
							}
						>
							<FormatListBulletedSharpIcon
								style={{ height: "auto" }}
								color="action"
							/>
						</Button>
					</Grid2>
				),
			},
		],
		[canEdit],
	);

	const getAreaContextMenuItems = useCallback(
		(e: GetContextMenuItemsParams<TaskListArea>) => {
			const row = e?.node?.data;
			const result: MenuItemDef[] = [];
			if (canEdit && selected) {
				result.push({
					name: "Add Area",
					action: () => setAdding(selected),
					icon: '<i class="fas fa-plus"></i>',
				});
			}
			if (row) {
				result.push({
					name: "Open Task List",
					action: () => navigate(`/task-list/area/${row.AssetId}`),
					icon: '<i class="fas fa-external-link-alt"></i>',
				});
				if (canEdit) {
					result.push({
						name: "Remove Area",
						action: () => setRemoving(row),
						icon: '<i class="fas fa-trash"></i>',
					});
				}
			}
			return result;
		},
		[canEdit, navigate, selected],
	);

	const closeModal = (savedMessage?: string) => {
		setAdding(null);
		setRemoving(null);
		if (savedMessage) {
			setNotification(savedMessage);
			queryClient.invalidateQueries({
				queryKey: TASK_LIST_AREAS_QUERY_KEYS.all,
			});
			// The task list reads the same flag, so its cached pages are stale now
			queryClient.invalidateQueries({ queryKey: ["taskList"] });
		}
	};

	const errorMessage =
		error && error !== dismissedError ? (error as Error).message : "";

	return (
		<>
			<HelmetDexteel title="Task List Areas - Configuration" />
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					height: "calc(100vh - 90px)",
					padding: 1,
					gap: 1,
				}}
			>
				<Typography
					variant="h5"
					style={{ fontWeight: 600 }}
					sx={{ flexShrink: 0 }}
				>
					Task List Areas
				</Typography>

				<Group
					id="task-list-areas-group"
					orientation="horizontal"
					defaultLayout={savedLayout ?? DEFAULT_LAYOUT}
					onLayoutChange={onLayoutChange}
					style={{ flex: 1, width: "100%", minHeight: 0 }}
				>
					<Panel id="left" defaultSize={38} minSize={25}>
						<Paper
							elevation={1}
							sx={{
								height: "100%",
								p: 1,
								borderRadius: "0.75rem",
								display: "flex",
								flexDirection: "column",
								gap: 1,
								boxSizing: "border-box",
							}}
						>
							<Box>
								<Typography variant="h6" sx={{ fontWeight: 600 }}>
									Default Areas
								</Typography>
								<Typography variant="body2" sx={{ color: "#666" }}>
									The Task List opens on each user's default area and shows one
									section per area inside it. Pick a default area to see its
									sections{canEdit ? " and add or remove them." : "."}
								</Typography>
							</Box>
							<Box sx={{ flex: 1, minHeight: 0 }}>
								<AgGridReact<DefaultArea>
									rowData={defaultAreas}
									columnDefs={defaultAreaColumns}
									defaultColDef={globalDefaultColDef}
									components={{ agGridTooltip: AgGridTooltip }}
									tooltipShowDelay={300}
									loading={isLoading}
									getRowId={(p) => String(p.data.AssetId)}
									rowSelection="single"
									rowStyle={{ cursor: "pointer" }}
									onRowClicked={(e) =>
										e.data && setDefaultAreaId(e.data.AssetId)
									}
									// Keep the picked default area highlighted after every reload
									onFirstDataRendered={(e) =>
										e.api
											.getRowNode(String(defaultAreaId))
											?.setSelected(true, true)
									}
									onRowDataUpdated={(e) =>
										e.api
											.getRowNode(String(defaultAreaId))
											?.setSelected(true, true)
									}
									overlayNoRowsTemplate="No default areas"
								/>
							</Box>
						</Paper>
					</Panel>

					<Separator
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							cursor: "col-resize",
							width: 16,
						}}
					>
						<Box
							sx={{
								width: 8,
								height: 48,
								borderRadius: 1,
								backgroundColor: "#e0e0e0",
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								justifyContent: "center",
								gap: "3px",
								transition: "background-color 0.2s",
								"&:hover": {
									backgroundColor: "primary.main",
									"& .grip-dot": { backgroundColor: "#fff" },
								},
							}}
						>
							{[...Array(5)].map((_, i) => (
								<Box
									// biome-ignore lint/suspicious/noArrayIndexKey: static decoration
									key={i}
									className="grip-dot"
									sx={{
										width: 4,
										height: 4,
										borderRadius: "50%",
										backgroundColor: "#9e9e9e",
										transition: "background-color 0.2s",
									}}
								/>
							))}
						</Box>
					</Separator>

					<Panel id="right" defaultSize={62} minSize={35}>
						<Paper
							elevation={1}
							sx={{
								height: "100%",
								p: 1,
								borderRadius: "0.75rem",
								display: "flex",
								flexDirection: "column",
								gap: 1,
								boxSizing: "border-box",
							}}
						>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									gap: 1,
								}}
							>
								<Box>
									<Typography variant="h6" sx={{ fontWeight: 600 }}>
										{selected
											? `Task list of ${selected.AssetName}`
											: "Task list"}
									</Typography>
									{selected && (
										<Typography variant="body2" sx={{ color: "#666" }}>
											{selected.Users === 0
												? "Nobody has it as default area yet."
												: `${selected.Users} user${selected.Users === 1 ? " opens" : "s open"} the Task List here.`}{" "}
											Each area includes everything below it.
										</Typography>
									)}
								</Box>
								{canEdit && selected && (
									<AddButton onClick={() => setAdding(selected)} />
								)}
							</Box>

							{selected && selected.AreasShown === 0 && (
								<Alert severity="info">
									No area inside {selected.AssetName}.{" "}
									{selected.FallbackAreaName
										? `Its task list shows ${selected.FallbackAreaName} instead.`
										: "Its task list is empty."}
								</Alert>
							)}

							<Box sx={{ flex: 1, minHeight: 0 }}>
								<AgGridReact<TaskListArea>
									rowData={areas}
									columnDefs={areaColumns}
									defaultColDef={globalDefaultColDef}
									components={{ agGridTooltip: AgGridTooltip }}
									tooltipShowDelay={300}
									rowSelection="single"
									loading={isLoading || (isFetching && areas.length === 0)}
									getContextMenuItems={getAreaContextMenuItems}
									getRowId={(p) => String(p.data.AssetId)}
									overlayNoRowsTemplate="No areas inside this default area"
								/>
							</Box>
						</Paper>
					</Panel>
				</Group>
			</Box>

			<AddTaskListArea
				defaultArea={adding}
				defaultAreas={defaultAreas}
				onHide={closeModal}
			/>
			<RemoveTaskListArea area={removing} onHide={closeModal} />

			<ErrorModal
				error={errorMessage}
				onHide={() => setDismissedError(error)}
			/>

			<Snackbar
				open={!!notification}
				autoHideDuration={2500}
				onClose={() => setNotification("")}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="success" onClose={() => setNotification("")}>
					{notification}
				</Alert>
			</Snackbar>
		</>
	);
};
